import { useState } from "react";

interface FactoryUser {
  name: string;
  email: string;
  phone?: string;
  company: string;
  country: string;
  flag: string;
  address: string;
}

interface Props {
  onClose: () => void;
  onAuth: (user: FactoryUser) => void;
}

const countries = [
  { name: "China", flag: "🇨🇳" },
  { name: "India", flag: "🇮🇳" },
  { name: "Vietnam", flag: "🇻🇳" },
  { name: "Bangladesh", flag: "🇧🇩" },
  { name: "Turkey", flag: "🇹🇷" },
  { name: "Indonesia", flag: "🇮🇩" },
  { name: "Mexico", flag: "🇲🇽" },
  { name: "Brazil", flag: "🇧🇷" },
  { name: "South Korea", flag: "🇰🇷" },
  { name: "Japan", flag: "🇯🇵" },
  { name: "Germany", flag: "🇩🇪" },
  { name: "USA", flag: "🇺🇸" },
  { name: "Italy", flag: "🇮🇹" },
  { name: "Pakistan", flag: "🇵🇰" },
  { name: "Thailand", flag: "🇹🇭" },
];

export default function FactoryAuthModal({ onClose, onAuth }: Props) {
  const [mode, setMode] = useState<"login" | "signup">("login");
  const [authMethod, setAuthMethod] = useState<"email" | "phone">("email");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [password, setPassword] = useState("");
  const [company, setCompany] = useState("");
  const [country, setCountry] = useState("China");
  const [address, setAddress] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const selectedCountry = countries.find(c => c.name === country) || countries[0];

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    if (mode === "signup") {
      if (!name || !company || !address) { setError("Please fill in all required fields."); return; }
      if (authMethod === "email" && !email) { setError("Please enter your email."); return; }
      if (authMethod === "phone" && !phone) { setError("Please enter your phone number."); return; }
    } else {
      if (authMethod === "email" && !email) { setError("Please enter your email."); return; }
      if (authMethod === "phone" && !phone) { setError("Please enter your phone number."); return; }
      if (!password) { setError("Please enter your password."); return; }
    }
    setLoading(true);
    setTimeout(() => {
      setLoading(false);
      onAuth({
        name: name || "Factory Owner",
        email: email || "",
        phone: phone || undefined,
        company: company || "My Factory Co.",
        country,
        flag: selectedCountry.flag,
        address: address || "Factory Industrial Zone",
      });
    }, 1200);
  };

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-md max-h-[92vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-5 border-b border-gray-100">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 flex items-center justify-center bg-amber-50 rounded-xl">
              <i className="ri-store-2-line text-amber-500 text-xl"></i>
            </div>
            <div>
              <h2 className="font-extrabold text-gray-900 text-base">Factory Portal</h2>
              <p className="text-xs text-gray-400">Post products · Reach 600M+ buyers</p>
            </div>
          </div>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 cursor-pointer">
            <i className="ri-close-line text-xl"></i>
          </button>
        </div>

        <div className="px-6 py-5">
          {/* Mode toggle */}
          <div className="flex bg-gray-100 rounded-full p-1 mb-5">
            <button
              onClick={() => setMode("login")}
              className={`flex-1 py-2 text-sm font-bold rounded-full transition-all cursor-pointer whitespace-nowrap ${mode === "login" ? "bg-white text-gray-900" : "text-gray-500"}`}
            >
              Sign In
            </button>
            <button
              onClick={() => setMode("signup")}
              className={`flex-1 py-2 text-sm font-bold rounded-full transition-all cursor-pointer whitespace-nowrap ${mode === "signup" ? "bg-white text-gray-900" : "text-gray-500"}`}
            >
              Sign Up
            </button>
          </div>

          {/* Auth method toggle */}
          <div className="flex gap-2 mb-5">
            <button
              onClick={() => setAuthMethod("email")}
              className={`flex-1 flex items-center justify-center gap-1.5 py-2 text-xs font-semibold rounded-xl border-2 transition-all cursor-pointer whitespace-nowrap ${authMethod === "email" ? "border-amber-400 bg-amber-50 text-amber-600" : "border-gray-200 text-gray-500"}`}
            >
              <i className="ri-mail-line"></i> Email
            </button>
            <button
              onClick={() => setAuthMethod("phone")}
              className={`flex-1 flex items-center justify-center gap-1.5 py-2 text-xs font-semibold rounded-xl border-2 transition-all cursor-pointer whitespace-nowrap ${authMethod === "phone" ? "border-amber-400 bg-amber-50 text-amber-600" : "border-gray-200 text-gray-500"}`}
            >
              <i className="ri-phone-line"></i> Phone
            </button>
          </div>

          <form onSubmit={handleSubmit} className="flex flex-col gap-3.5">
            {mode === "signup" && (
              <>
                <div>
                  <label className="text-xs font-semibold text-gray-600 mb-1 block">Your Name *</label>
                  <input
                    required
                    value={name}
                    onChange={e => setName(e.target.value)}
                    placeholder="Full name"
                    className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm outline-none focus:border-amber-400 transition-colors"
                  />
                </div>
                <div>
                  <label className="text-xs font-semibold text-gray-600 mb-1 block">Company / Factory Name *</label>
                  <input
                    required
                    value={company}
                    onChange={e => setCompany(e.target.value)}
                    placeholder="e.g. Guangzhou Electronics Co., Ltd."
                    className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm outline-none focus:border-amber-400 transition-colors"
                  />
                </div>
                <div>
                  <label className="text-xs font-semibold text-gray-600 mb-1 block">Country *</label>
                  <select
                    value={country}
                    onChange={e => setCountry(e.target.value)}
                    className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm outline-none focus:border-amber-400 cursor-pointer bg-white"
                  >
                    {countries.map(c => (
                      <option key={c.name} value={c.name}>{c.flag} {c.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="text-xs font-semibold text-gray-600 mb-1 block">Factory Address *</label>
                  <input
                    required
                    value={address}
                    onChange={e => setAddress(e.target.value)}
                    placeholder="e.g. No.88 Industrial Park, Guangzhou, China"
                    className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm outline-none focus:border-amber-400 transition-colors"
                  />
                </div>
              </>
            )}

            {authMethod === "email" ? (
              <div>
                <label className="text-xs font-semibold text-gray-600 mb-1 block">Email Address *</label>
                <input
                  type="email"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  placeholder="factory@company.com"
                  className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm outline-none focus:border-amber-400 transition-colors"
                />
              </div>
            ) : (
              <div>
                <label className="text-xs font-semibold text-gray-600 mb-1 block">Phone Number *</label>
                <input
                  type="tel"
                  value={phone}
                  onChange={e => setPhone(e.target.value)}
                  placeholder="+86 138 0000 0000"
                  className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm outline-none focus:border-amber-400 transition-colors"
                />
              </div>
            )}

            <div>
              <label className="text-xs font-semibold text-gray-600 mb-1 block">Password *</label>
              <input
                type="password"
                value={password}
                onChange={e => setPassword(e.target.value)}
                placeholder="••••••••"
                className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm outline-none focus:border-amber-400 transition-colors"
              />
            </div>

            {error && (
              <p className="text-red-500 text-xs bg-red-50 px-3 py-2 rounded-lg">{error}</p>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-amber-400 hover:bg-amber-500 disabled:bg-amber-300 text-white font-bold py-3 rounded-full transition-colors cursor-pointer whitespace-nowrap mt-1"
            >
              {loading ? (
                <span className="flex items-center justify-center gap-2">
                  <i className="ri-loader-4-line animate-spin"></i> Processing...
                </span>
              ) : mode === "login" ? "Sign In to Factory Portal" : "Create Factory Account"}
            </button>
          </form>

          {/* Benefits */}
          {mode === "signup" && (
            <div className="mt-5 bg-amber-50 rounded-xl p-4">
              <p className="text-xs font-bold text-amber-700 mb-2">Why join CrownGlobal?</p>
              <ul className="flex flex-col gap-1.5">
                {[
                  "Reach 600 Million+ buyers worldwide",
                  "Post unlimited products for free",
                  "UPS tracked delivery from your warehouse",
                  "Buyer-seller messaging built in",
                  "Social Stars promote your products",
                ].map(b => (
                  <li key={b} className="flex items-center gap-2 text-xs text-amber-700">
                    <i className="ri-checkbox-circle-fill text-amber-400 shrink-0"></i>{b}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
